//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Dialog Map
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   27.03.23 Sean Flook          WI40628 Correctly call onWizardSetCoordinate when dragging the pin on place on map page.
//    003   07.09.23 Sean Flook                  Removed unnecessary wait.
//    004   06.10.23 Sean Flook                  Use colour variables.
//    005   10.11.23 Sean Flook        IMANN-175 Changes required for Move BLPU seed point.
//    006   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system.
//    007   30.11.23 Sean Flook                  Changes required to handle Scottish authorities.
//    008   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    009   16.01.24 Sean Flook                  Changes required to fix warnings.
//    010   25.01.24 Sean Flook                  Changes required after UX review.
//    011   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    012   20.03.24 Sean Flook                  Changes required to load shape files.
//    013   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    014   16.04.24 Sean Flook        IMANN-377 Added background properties and streets.
//    015   16.04.24 Sean Flook                  When loading a SHP file use the mapContext to retain the information and display the layer in the map.
//    016   07.02.24 Sean Flook        IMANN-377 Changes required to support viaEuropa mapping for OneScotland.
//    017   03.05.24 Sean Flook                  Call getBaseMapLayers.
//    018   20.05.24 Sean Flook        IMANN-476 Check view has been created first in fadeVisibilityOn.
//    019   18.06.24 Sean Flook        IMANN-599 Use the correct classification when moving BLPUs for Scottish authorities.
//    020   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    021   08.07.24 Sean Flook        IMANN-728 Pass in the new parameter to onExtentChange.
//    022   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.3.0
//    023   06.01.25 Sean Flook       IMANN-1123 Changed Multiple properties to Multiple addresses in popup.
//    024   06.01.25 Sean Flook       IMANN-1123 Changed issue number above.
//    025   07.01.25 Sean Flook       IMANN-1126 If the user changes the zoom level remember it.
//    026   07.01.25 Sean Flook       IMANN-1123 Sort properties on logical status to try and get the cluster symbol correct.
//    027   10.01.25 Sean Flook       IMANN-1126 Also handle when user uses the mouse wheel to zoom on the map.
//endregion Version 1.0.3.0
//region Version 1.0.6.0
//    028   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//    029   22.07.25 Sean Flook       IMANN-1855 Set the mapProgress parameter on the ADSCircularProgress component for loading the map.
//endregion Version 1.0.6.0
//region Version 1.0.6.4
//    030   30.09.25 Sean Flook       IMANN-2074 When setting the wizard point data, use numbers for the coordinates.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    031   13.06.25 Sean Flook       IMANN-1812 Upgraded to the latest ESRI controls and changed name of file.
//    032   15.09.25 Sean Flook       IMANN-1812 Changes required to add slider to change layer opacity and to remember the value set between sessions.
//    033   16.09.25 Sean Flook       IMANN-1962 Make the background provenance layer visible by default and added code to be able to save the visibility of background layers between sessions.
//    034   24.09.25 Sean Flook       IMANN-1031 Various changes to bring map up to date and improve performance.
//    035   24.10.25 Sean Flook       IMANN-1129 Changes required to style shape files when loaded.
//    036   24.10.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image files.
//    037   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    038   31.10.25 Sean Flook       IMANN-1812 The expand component will automatically use the icon of the component placed within the Expand if it has one.
//    039   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    040   07.11.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image file and world file.
//    041   11.11.25 Sean Flook       IMANN-2092 Further changes required to display the Jersey basemap.
//    042   12.11.25 Sean Flook       IMANN-2092 Correctly display the coordinates at the cursor.
//    043   17.12.25 Sean Flook       IMANN-2196 Added the information control and the selection control, Also, a few required bug fixes.
//    044   21.01.26 Sean Flook       IMANN-2204 Replaced calls to GetWktCoordinates with wktToGeometry.
//    045   04.02.26 Joshua McCormick IMANN-2261 Fixed street records by USRN to prevent duplicate popup entries.
//    046   11.02.26 Sean Flook       IMANN-2261 Undone above changes and ensured the add features runs after the delete features.
//    047   18.02.26 Sean Flook       IMANN-2327 Disable the Finish button until the map has finished updating.
//    048   16.03.26 Sean Flook       IMANN-2327 Added new function onMapUpdated to prevent the Finish button from being enabled prematurely.
//    049   18.03.26 Sean Flook       IMANN-2212 If we have a SHP file or a Georeferenced image file, load again when recreating the map.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    050   27.04.26 Sean Flook       IMANN-2411 Correctly compare UPRNs.
//    051   28.04.26 Sean Flook       IMANN-2415 Removed useSearchExtent as not required.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";

import MapContext from "../context/mapContext";
import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";
import InformationContext from "../context/informationContext";
import SelectionContext from "../context/selectionContext";

import {
  backgroundStreetLayerName,
  unassignedEsusLayerName,
  backgroundPropertyLayerName,
  labelLayerName,
  sketchLayerName,
  streetRenderer,
  PropertyFeatureReduction,
  backgroundPropertyRenderer,
  propertyRenderer,
  unassignedEsusLayerTitle,
  backgroundStreetLayerTitle,
  backgroundPropertyLayerTitle,
  backgroundProvenanceLayerTitle,
  sketchLayerTitle,
  labelLayerTitle,
  setLayerOpacity,
  getLayerOpacity,
  getLayerVisibility,
  setLayerVisibility,
  DEFAULT_SIMPLE_POINT_JSON,
  DEFAULT_SIMPLE_LINE_JSON,
  DEFAULT_SIMPLE_POLYGON_JSON,
  fixSymbolJson,
  getUniqueValuesFromRendererJson,
  getCoordRefSystemName,
} from "../utils/MapUtils";
import { ArraysEqual, wktToGeometry, defaultMapLayerIds, getBaseMapLayers } from "../utils/HelperUtils";
import { GetStreetStateLabel, GetStreetTypeLabel, streetToTitleCase } from "../utils/StreetUtils";
import { GetClassificationLabel, GetLPILogicalStatusLabel, addressToTitleCase } from "../utils/PropertyUtils";

import shp from "shpjs";

import "@arcgis/map-components/dist/components/arcgis-distance-measurement-2d";
import "@arcgis/map-components/dist/components/arcgis-area-measurement-2d";
import "@arcgis/map-components/dist/components/arcgis-coordinate-conversion";
import "@arcgis/map-components/dist/components/arcgis-expand";
import "@arcgis/map-components/dist/components/arcgis-basemap-layer-list";
import "@arcgis/map-components/dist/components/arcgis-layer-list";
import "@arcgis/map-components/dist/components/arcgis-map";
import "@arcgis/map-components/dist/components/arcgis-placement";
import "@arcgis/map-components/dist/components/arcgis-scale-bar";
import "@arcgis/map-components/dist/components/arcgis-sketch";
import "@arcgis/map-components/dist/components/arcgis-zoom";

import WMSLayer from "@arcgis/core/layers/WMSLayer";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer.js";
import Basemap from "@arcgis/core/Basemap";
import LocalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/LocalBasemapsSource.js";
import Map from "@arcgis/core/Map";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MediaLayer from "@arcgis/core/layers/MediaLayer";
import ImageElement from "@arcgis/core/layers/support/ImageElement";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";

import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer.js";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";

import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol.js";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol.js";

import Extent from "@arcgis/core/geometry/Extent";
import ExtentAndRotationGeoreference from "@arcgis/core/layers/support/ExtentAndRotationGeoreference";
import CornersGeoreference from "@arcgis/core/layers/support/CornersGeoreference.js";
import TileInfo from "@arcgis/core/layers/support/TileInfo";
import Conversion from "@arcgis/core/widgets/CoordinateConversion/support/Conversion";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import { XMLParser } from "fast-xml-parser";

import { Snackbar, Alert, Box, Stack } from "@mui/material";
import { CalciteButton } from "@esri/calcite-components-react";
import ADSPlaceOnMapControl from "./ADSPlaceOnMapControl";
import ADSSelectionControl from "./ADSSelectionControl";
import ADSInformationControl from "./ADSInformationControl";
import ADSCircularProgress from "../components/ADSCircularProgress";
import UploadShpFileDialog from "../dialogs/UploadShpFileDialog";

import { GetPropertyMapSymbol } from "../utils/ADSMapSymbols";

import { GetAlertStyle, GetAlertIcon, GetAlertSeverity } from "../utils/ADSStyles";

import { adsLightGreyA50, adsWhite } from "../utils/ADSColours";
import Format from "@arcgis/core/widgets/CoordinateConversion/support/Format";

ADSMapDialog.propTypes = {
  startExtent: PropTypes.object,
  data: PropTypes.array.isRequired,
  placeOnMapData: PropTypes.object,
  isChild: PropTypes.bool,
  isRange: PropTypes.bool,
  displayPlaceOnMap: PropTypes.bool,
  moveBlpu: PropTypes.bool,
  onPlaceOnMapDataChange: PropTypes.func,
  onMapUpdated: PropTypes.func,
};

function ADSMapDialog({
  startExtent,
  data,
  placeOnMapData,
  isChild = false,
  isRange = false,
  displayPlaceOnMap = false,
  moveBlpu = false,
  onPlaceOnMapDataChange,
  onMapUpdated,
}) {
  const mapContext = useContext(MapContext);
  const settingsContext = useContext(SettingsContext);
  const userContext = useRef(useContext(UserContext));
  const informationContext = useContext(InformationContext);
  const selectionContext = useContext(SelectionContext);

  const [arcgisDialogMap, setArcgisDialogMap] = useState(null);
  const arcgisDialogLayerList = useRef(null);
  const arcgisDialogCoordinateConversion = useRef(null);
  const arcgisDialogSketch = useRef(null);
  const baseMapLayers = useRef(null);
  const [baseMapGallerySource, setBaseMapGallerySource] = useState(new LocalBasemapsSource({ basemaps: [] }));
  const layersAdded = useRef(false);

  const saveResult = useRef(null);
  const saveType = useRef(null);
  const saveOpenRef = useRef(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const failedValidation = useRef(null);
  const featuresDownloaded = useRef(0);

  const [sketchCreateTools, setSketchCreateTools] = useState([]);

  const baseMappingLayerIds = useRef([]);
  const baseLayersSnapEsu = useRef([]);
  const baseLayersSnapBlpu = useRef([]);
  const baseLayersSnapExtent = useRef([]);
  const baseLayersFeature = useRef([]);
  const loadedShpFileTitles = useRef([]);
  const loadedShpFileIds = useRef([]);
  const loadedGeoFileTitles = useRef([]);
  const loadedGeoFileIds = useRef([]);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);
  const isScottish = useRef(settingsContext.isScottish);
  const backgroundExtent = useRef(
    startExtent
      ? {
          xmin: startExtent.xmin,
          ymin: startExtent.ymin,
          xmax: startExtent.xmax,
          ymax: startExtent.ymax,
          spatialReference: startExtent.spatialReference,
          zoom: startExtent.zoom,
        }
      : null
  );
  const highlightProperty = useRef(null);
  const currentHighlightedProperties = useRef([]);

  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);

  const [initialisingMap, setInitialisingMap] = useState(true);
  const [mapUpdating, setMapUpdating] = useState(false);

  const viewReadyEvent = useRef(false);
  const viewChangeEvent = useRef(false);
  const viewClickEvent = useRef(false);
  const layerListTriggerEvent = useRef(false);
  const sketchUpdateEvent = useRef(false);

  const currentMapExtent = useRef(null);

  const backgroundStreetData = useRef(null);
  const unassignedEsusData = useRef(null);
  const backgroundPropertyData = useRef(null);
  const [pointData, setPointData] = useState(null);

  const backgroundStreetLayer = useRef(null);
  const backgroundPropertyLayer = useRef(null);
  const unassignedEsusLayer = useRef(null);
  const pointLayer = useRef(null);
  const sketchLayer = useRef(
    new GraphicsLayer({
      id: sketchLayerName,
      copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
      title: sketchLayerTitle,
      listMode: "hide",
    })
  );

  const oldBackgroundStreets = useRef([]);
  const oldZoomBackgroundStreet = useRef(null);
  const oldUnassignedEsus = useRef([]);
  const oldBackgroundProperties = useRef([]);
  const oldZoomBackgroundProperty = useRef(null);
  const oldPoints = useRef([]);

  const [updatingMap, setUpdatingMap] = useState(true);

  // [point, area]
  const [placeStyle, setPlaceStyle] = useState(null);
  // [linear, streetGeometry, elliptical]
  const [areaStyle, setAreaStyle] = useState(null);
  // [topLeft, top, topRight, right, bottomRight, bottom, bottomLeft, left]
  const [startPoint, setStartPoint] = useState(null);
  // [clockwise, antiClockwise]
  const [direction, setDirection] = useState(null);

  const placeOnMapOpen = useRef(displayPlaceOnMap);

  if (updatingMap && mapContext.updatingData === 0) mapContext.onIncreaseDataChange();

  /**
   * Method used to handle the view ready event.
   */
  const handleViewReady = async () => {
    if (backgroundExtent.current || startExtent) {
      let newExtent = new Extent();
      newExtent.xmin = backgroundExtent.current ? backgroundExtent.current.xmin : startExtent.xmin;
      newExtent.ymin = backgroundExtent.current ? backgroundExtent.current.ymin : startExtent.ymin;
      newExtent.xmax = backgroundExtent.current ? backgroundExtent.current.xmax : startExtent.xmax;
      newExtent.ymax = backgroundExtent.current ? backgroundExtent.current.ymax : startExtent.ymax;
      newExtent.spatialReference = backgroundExtent.current
        ? backgroundExtent.current.spatialReference
        : startExtent.spatialReference;
      arcgisDialogMap.view.extent = newExtent;
      arcgisDialogMap.zoom = backgroundExtent.current ? backgroundExtent.current.zoom : startExtent.zoom;

      if (!mapContext.currentExtent) {
        mapContext.onExtentChange({
          xmin: backgroundExtent.current ? backgroundExtent.current.xmin : startExtent.xmin,
          ymin: backgroundExtent.current ? backgroundExtent.current.ymin : startExtent.ymin,
          xmax: backgroundExtent.current ? backgroundExtent.current.xmax : startExtent.xmax,
          ymax: backgroundExtent.current ? backgroundExtent.current.ymax : startExtent.ymax,
          zoom: backgroundExtent.current ? backgroundExtent.current.zoom : startExtent.zoom,
          spatialReference: backgroundExtent.current
            ? backgroundExtent.current.spatialReference
            : startExtent.spatialReference,
          hasProperties: userContext.current.currentUser.hasProperty,
        });
      }
    }

    if (!arcgisDialogLayerList.current && arcgisDialogMap.querySelector(`.arcgis-layer-list-dialog`))
      arcgisDialogLayerList.current = arcgisDialogMap.querySelector(`.arcgis-layer-list-dialog`);
    if (
      !arcgisDialogCoordinateConversion.current &&
      arcgisDialogMap.querySelector(`.arcgis-coordinate-conversion-dialog`)
    )
      arcgisDialogCoordinateConversion.current = arcgisDialogMap.querySelector(`.arcgis-coordinate-conversion-dialog`);
    if (!arcgisDialogSketch.current && arcgisDialogMap.querySelector(`.arcgis-sketch-dialog`))
      arcgisDialogSketch.current = arcgisDialogMap.querySelector(`.arcgis-sketch-dialog`);

    // Set the layer list event listeners when the layer list is ready
    if (arcgisDialogLayerList.current) {
      if (!layerListTriggerEvent.current) {
        arcgisDialogLayerList.current.addEventListener("arcgisTriggerAction", handleTriggerAction);
        layerListTriggerEvent.current = true;
      }
    }

    // Set the coordinate conversion component formats and conversions
    if (arcgisDialogCoordinateConversion.current) {
      const coordRefSystemName = await getCoordRefSystemName(spatialReferenceWkid.current);
      if (
        arcgisDialogCoordinateConversion.current.formats &&
        arcgisDialogCoordinateConversion.current.formats.length > 0
      ) {
        let filteredFormats = arcgisDialogCoordinateConversion.current.formats.items.filter(
          (f) => f.name === "basemap" || f.name === "xy"
        );
        filteredFormats[filteredFormats.findIndex((f) => f.name === "basemap")].label = coordRefSystemName;
        filteredFormats[filteredFormats.findIndex((f) => f.name === "xy")].label = "Long/Lat";
        arcgisDialogCoordinateConversion.current.formats.items = filteredFormats;
      }

      if (
        arcgisDialogCoordinateConversion.current.conversions &&
        arcgisDialogCoordinateConversion.current.conversions.length > 0
      ) {
        arcgisDialogCoordinateConversion.current.conversions.removeAll();
        const f = arcgisDialogCoordinateConversion.current.formats.items.find((f) => f.name === "basemap");
        if (f.spatialReference.wkid === spatialReferenceWkid.current) {
          arcgisDialogCoordinateConversion.current.conversions.add(new Conversion({ format: f }));
        } else {
          const baseMapFormat = new Format({
            name: coordRefSystemName,
            spatialReference: { wkid: spatialReferenceWkid.current },
          });
          arcgisDialogCoordinateConversion.current.conversions.add(new Conversion({ format: baseMapFormat }));
        }
      }
    }

    // Set the sketch event listeners and initialise the sketch layer
    if (arcgisDialogSketch.current) {
      if (!sketchUpdateEvent.current) {
        arcgisDialogSketch.current.addEventListener("arcgisUpdate", handleSketchUpdate);
        sketchUpdateEvent.current = true;
      }

      arcgisDialogSketch.current.layer = sketchLayer.current;
      arcgisDialogSketch.current.layer.popupEnabled = false;
      arcgisDialogSketch.current.layer.listMode = "hide";
      setSketchCreateTools([]);
    }

    setUpdatingMap(false);
    if (mapContext.updatingData > 0) mapContext.onDecreaseDataChange();
  };

  /**
   * Method used to handle the view change event.
   */
  const handleViewChange = (event) => {
    const { view, zoom, extent } = event.target;

    if (
      view &&
      view.stationary &&
      zoom > -1 &&
      extent &&
      extent.xmin >= 0 &&
      extent.ymin >= 0 &&
      (!currentMapExtent.current ||
        currentMapExtent.current.xmin !== extent.xmin ||
        currentMapExtent.current.ymin !== extent.ymin ||
        currentMapExtent.current.xmax !== extent.xmax ||
        currentMapExtent.current.ymax !== extent.ymax ||
        currentMapExtent.current.zoom !== zoom)
    ) {
      currentMapExtent.current = {
        xmin: extent.xmin,
        ymin: extent.ymin,
        xmax: extent.xmax,
        ymax: extent.ymax,
        zoom: zoom,
      };

      mapContext.onExtentChange({
        xmin: extent.xmin,
        ymin: extent.ymin,
        xmax: extent.xmax,
        ymax: extent.ymax,
        zoom: zoom,
        spatialReference: { wkid: spatialReferenceWkid.current },
        hasProperties: userContext.current.currentUser.hasProperty,
      });
    }
  };

  /**
   * Method used to handle the view click event.
   *
   * @param {object} event The view click event
   */
  const handleViewClick = (event) => {
    const opts = { include: sketchLayer.current };
    arcgisDialogMap.view.hitTest(event.detail, opts).then(function (response) {
      if (response.results.length === 0) {
        // Only set coordinates if user has not clicked on a property pin
        if (
          placeOnMapData &&
          placeOnMapData.placeStyle === "point" &&
          (!mapContext.currentWizardPoint ||
            mapContext.currentWizardPoint.x !== Number(event.detail.mapPoint.x.toFixed(4)) ||
            mapContext.currentWizardPoint.y !== Number(event.detail.mapPoint.y.toFixed(4)))
        ) {
          mapContext.onWizardSetCoordinate({
            x: Number(event.detail.mapPoint.x.toFixed(4)),
            y: Number(event.detail.mapPoint.y.toFixed(4)),
            pointId: null,
            placeOnMapData: placeOnMapData,
          });
        } else if (!placeOnMapData) {
          mapContext.onPinSelected(null);
        }
      } else if (!placeOnMapData) {
        mapContext.onPinSelected(response.results[0].graphic.attributes.PropertyId);
      }
    });
  };

  /**
   * Method used to handle the trigger action event from the layer list.
   *
   * @param {object} event The event object.
   */
  const handleTriggerAction = (event) => {
    event.preventDefault();
    const { action, item } = event.detail;
    switch (action.id) {
      case "close-shp-file":
        mapContext.onUnloadShpFile(item.layer.id);
        break;

      case "close-geo-file":
        mapContext.onUnloadGeoFile(item.layer.id);
        break;

      default:
        break;
    }
  };

  // /**
  //  * Method used to handle the sketch update event.
  //  *
  //  * @param {SketchUpdateEvent} event The sketch update event.
  //  */
  const handleSketchUpdate = (event) => {
    event.stopPropagation();
    if (!event.detail.aborted) {
      // get the graphic as it is being updated
      const graphic = event.detail.graphics[0];

      if (graphic.geometry) {
        switch (graphic.geometry.type) {
          case "point":
            if (
              event.detail.type === "undo" ||
              event.detail.type === "redo" ||
              (event.detail.toolEventInfo && event.detail.toolEventInfo.type === "move-stop")
            ) {
              if (
                (!placeOnMapData || placeOnMapData.placeStyle === "point") &&
                (!mapContext.currentWizardPoint ||
                  mapContext.currentWizardPoint.x !== Number(graphic.geometry.x.toFixed(4)) ||
                  mapContext.currentWizardPoint.y !== Number(graphic.geometry.y.toFixed(4)))
              ) {
                mapContext.onWizardSetCoordinate({
                  x: Number(graphic.geometry.x.toFixed(4)),
                  y: Number(graphic.geometry.y.toFixed(4)),
                  pointId: !placeOnMapData ? graphic.attributes.PropertyId : null,
                  placeOnMapData: placeOnMapData,
                });
              }
            }
            break;

          default:
            break;
        }
      }
    }
  };

  // Set the map event listeners when the map is ready
  if (arcgisDialogMap) {
    if (!viewReadyEvent.current) {
      arcgisDialogMap.addEventListener("arcgisViewReadyChange", handleViewReady);
      viewReadyEvent.current = true;
    }

    if (!viewChangeEvent.current) {
      arcgisDialogMap.addEventListener("arcgisViewChange", handleViewChange);
      viewChangeEvent.current = true;
    }

    if (!viewClickEvent.current) {
      arcgisDialogMap.addEventListener("arcgisViewClick", handleViewClick);
      viewClickEvent.current = true;
    }
  }

  /**
   * Method to handle when the upload file button is clicked.
   */
  function handleUploadFile() {
    setOpenUploadFileDialog(true);
  }

  /**
   * Method used to create a new point graphic.
   *
   * @param {number} x The easting for the property.
   * @param {number} y The northing for the property.
   * @param {number} logicalStatus The logical status for the property.
   * @param {string} classification The classification for the property.
   * @param {boolean} highlighted True if the property should be highlighted; otherwise false.
   * @returns {object} The new point graphic object.
   */
  function createPointGraphic(x, y, id, logicalStatus, classification, highlighted = false) {
    const editPoint = new Point({
      type: "point",
      x: x,
      y: y,
      spatialReference: { wkid: spatialReferenceWkid.current },
    });

    const editPointGraphic = new Graphic({
      attributes: { PropertyId: id, typeName: "Point" },
      geometry: editPoint,
      symbol: GetPropertyMapSymbol(logicalStatus, classification, highlighted),
    });

    return editPointGraphic;
  }

  /**
   * Method used to define the actions available for the feature layers.
   *
   * @param {object} event The event object.
   */
  function defineActions(event) {
    const { item } = event;

    if (
      [
        unassignedEsusLayerTitle,
        backgroundStreetLayerTitle,
        backgroundPropertyLayerTitle,
        backgroundProvenanceLayerTitle,
      ].includes(item.title) ||
      loadedGeoFileTitles.current.includes(item.title)
    ) {
      const label = document.createElement("calcite-label");
      label.innerText = "Opacity";
      label.scale = "s";

      const slider = document.createElement("calcite-slider");
      slider.labelHandles = true;
      slider.labelTicks = true;
      slider.min = 0;
      slider.minLabel = "0";
      slider.max = 1;
      slider.maxLabel = "1";
      slider.scale = "s";
      slider.step = 0.01;
      slider.value = item.layer.opacity;
      slider.ticks = 0.5;

      slider.addEventListener("calciteSliderChange", () => {
        item.layer.opacity = slider.value;
        setLayerOpacity(item.title, true, slider.value);
      });

      label.appendChild(slider);

      item.panel = {
        content: label,
        icon: "sliders-horizontal",
        title: "Change layer opacity",
      };
    }

    if (loadedShpFileTitles.current.includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: "Remove layer",
            icon: "x-circle-f",
            id: "close-shp-file",
          },
        ],
      ];
    }

    if (loadedGeoFileTitles.current.includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: "Remove layer",
            icon: "x-circle-f",
            id: "close-geo-file",
          },
        ],
      ];
    }
  }

  /**
   * Method used to get the place on map data.
   *
   * @param {string} updatedField The field that has been updated.
   * @param {string} newValue The new value for the field.
   * @returns {object} The updated place on map data.
   */
  const getPlaceOnMapData = (updatedField, newValue) => {
    return {
      placeStyle: updatedField === "placeStyle" ? newValue : placeStyle,
      areaStyle: updatedField === "areaStyle" ? newValue : areaStyle,
      startPoint: updatedField === "startPoint" ? newValue : startPoint,
      direction: updatedField === "direction" ? newValue : direction,
    };
  };

  /**
   * Method used to handle when the place style is changed.
   *
   * @param {string} newValue The new value for the field.
   */
  const handlePlaceStyleChanged = (newValue) => {
    setPlaceStyle(newValue);

    if (onPlaceOnMapDataChange) onPlaceOnMapDataChange(getPlaceOnMapData("placeStyle", newValue));
  };

  /**
   * Method used to handle when the area style is changed.
   *
   * @param {string} newValue The new value for the field.
   */
  const handleAreaStyleChanged = (newValue) => {
    setAreaStyle(newValue);

    if (onPlaceOnMapDataChange) onPlaceOnMapDataChange(getPlaceOnMapData("areaStyle", newValue));
  };

  /**
   * Method used to handle when the start point is changed.
   *
   * @param {string} newValue The new value for the field.
   */
  const handleStartPointChanged = (newValue) => {
    setStartPoint(newValue);

    if (onPlaceOnMapDataChange) onPlaceOnMapDataChange(getPlaceOnMapData("startPoint", newValue));
  };

  /**
   * Method used to handle when the direction is changed.
   *
   * @param {string} newValue The new value for the field.
   */
  const handleDirectionChanged = (newValue) => {
    setDirection(newValue);

    if (onPlaceOnMapDataChange) onPlaceOnMapDataChange(getPlaceOnMapData("direction", newValue));
  };

  /**
   * Method to handle when the upload shp file dialog is closed.
   */
  const doUploadShpFile = (shpFile) => {
    const reader = new FileReader();
    reader.onload = function () {
      if (reader.readyState !== 2 || reader.error) {
        if (userContext.current.currentUser.showMessages)
          console.error(`[ERROR] failed to upload ${shpFile.title}`, {
            readyState: reader.readyState,
            error: reader.error,
          });
        saveResult.current = false;
        featuresDownloaded.current = shpFile.title;
        saveType.current = "uploadedFileError";
        saveOpenRef.current = true;
        setSaveOpen(true);
        return;
      } else {
        shp(reader.result).then(function (geojson) {
          let shpRenderer = null;
          if (shpFile.type === "shp" || !shpFile.layerFile) {
            switch (shpFile.geometryType) {
              case "point":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleMarkerSymbol.fromJSON(DEFAULT_SIMPLE_POINT_JSON),
                });
                break;
              case "line":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleLineSymbol.fromJSON(DEFAULT_SIMPLE_LINE_JSON),
                });
                break;
              case "polygon":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleFillSymbol.fromJSON(DEFAULT_SIMPLE_POLYGON_JSON),
                });
                break;
              default:
                break;
            }

            mapContext.onLoadShpFile(JSON.stringify(geojson), shpFile, shpRenderer);
            saveResult.current = true;
            featuresDownloaded.current = shpFile.title;
            saveType.current = "uploadedFile";
            saveOpenRef.current = true;
            setSaveOpen(true);
          } else {
            const layerReader = new FileReader();

            layerReader.onload = function () {
              if (layerReader.readyState !== 2 || layerReader.error) {
                if (userContext.current.currentUser.showMessages)
                  console.error(`[ERROR] failed to upload ${shpFile.layerFile}`, {
                    readyState: layerReader.readyState,
                    error: layerReader.error,
                  });
                saveResult.current = false;
                featuresDownloaded.current = shpFile.layerFile;
                saveType.current = "uploadedFileError";
                saveOpenRef.current = true;
                setSaveOpen(true);
                return;
              } else {
                let popupTitle = "";
                if (layerReader.result) {
                  if (shpFile.layerFile.name.endsWith(".lyrx")) {
                    const layerJson = JSON.parse(layerReader.result);
                    if (
                      layerJson &&
                      layerJson.layerDefinitions &&
                      layerJson.layerDefinitions.length > 0 &&
                      layerJson.layerDefinitions[0].renderer
                    ) {
                      switch (layerJson.layerDefinitions[0].renderer.type) {
                        case "simple":
                        case "CIMSimpleRenderer":
                          shpRenderer = SimpleRenderer.fromJSON(fixSymbolJson(layerJson.layerDefinitions[0].renderer));
                          break;

                        case "uniqueValue":
                        case "CIMUniqueValueRenderer":
                          shpRenderer = UniqueValueRenderer.fromJSON(
                            getUniqueValuesFromRendererJson(fixSymbolJson(layerJson.layerDefinitions[0].renderer))
                          );
                          break;

                        default:
                          saveResult.current = false;
                          featuresDownloaded.current = `${shpFile.layerFile.name} - [${layerJson.layerDefinitions[0].renderer.type}]`;
                          saveType.current = "uploadedRendererError";
                          saveOpenRef.current = true;
                          setSaveOpen(true);
                          break;
                      }
                      popupTitle = layerJson.layerDefinitions[0].featureTable.displayField || "";
                    }
                  } else if (shpFile.layerFile.name.endsWith(".qml")) {
                    const options = {
                      ignoreAttributes: false,
                      // attributeNamePrefix: "@_",
                      attributeNamePrefix: "",
                    };
                    const xmlParser = new XMLParser(options);
                    const layerJson = xmlParser.parse(layerReader.result);
                    if (layerJson && layerJson.qgis && layerJson.qgis["renderer-v2"]) {
                      const qgisRenderer = layerJson.qgis["renderer-v2"];
                      console.log("[SF] qgisRenderer", JSON.stringify(qgisRenderer));
                    }
                  }

                  if (shpRenderer) {
                    mapContext.onLoadShpFile(JSON.stringify(geojson), shpFile, shpRenderer, popupTitle);
                    saveResult.current = true;
                    featuresDownloaded.current = shpFile.title;
                    saveType.current = "uploadedFile";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  } else {
                    saveResult.current = false;
                    featuresDownloaded.current = shpFile.layerFile.name;
                    saveType.current = "uploadedRendererError";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  }
                }
              }
            };

            layerReader.readAsText(shpFile.layerFile);
          }
        });
      }
    };

    reader.readAsArrayBuffer(shpFile.file);
  };

  /**
   * Method to upload and process a georeferenced file.
   */
  const doUploadGeoFile = (geoFile) => {
    const reader = new FileReader();

    reader.onload = function () {
      if (reader.readyState !== 2 || reader.error) {
        if (userContext.current.currentUser.showMessages)
          console.error(`[ERROR] failed to upload ${geoFile.title}`, {
            readyState: reader.readyState,
            error: reader.error,
          });
        saveResult.current = false;
        featuresDownloaded.current = geoFile.title;
        saveType.current = "uploadedFileError";
        saveOpenRef.current = true;
        setSaveOpen(true);
        return;
      } else {
        if (geoFile.type === "geo" || !geoFile.worldFile) {
          mapContext.onLoadGeoFile(reader.result, geoFile);
          saveResult.current = true;
          featuresDownloaded.current = geoFile.title;
          saveType.current = "uploadedFile";
          saveOpenRef.current = true;
          setSaveOpen(true);
        } else {
          const worldReader = new FileReader();

          worldReader.onload = function () {
            if (worldReader.readyState !== 2 || worldReader.error) {
              if (userContext.current.currentUser.showMessages)
                console.error(`[ERROR] failed to upload ${geoFile.worldFile}`, {
                  readyState: worldReader.readyState,
                  error: worldReader.error,
                });
              saveResult.current = false;
              featuresDownloaded.current = geoFile.worldFile;
              saveType.current = "uploadedFileError";
              saveOpenRef.current = true;
              setSaveOpen(true);
              return;
            } else {
              if (worldReader.result) {
                const worldData = worldReader.result.split(/\r?\n/);
                if (worldData.length > 5) {
                  mapContext.onLoadGeoFile(reader.result, geoFile, worldData.slice(0, 6).map(Number));
                  saveResult.current = true;
                  featuresDownloaded.current = geoFile.title;
                  saveType.current = "uploadedFile";
                  saveOpenRef.current = true;
                  setSaveOpen(true);
                } else {
                  saveResult.current = false;
                  featuresDownloaded.current = geoFile.worldFile.name;
                  saveType.current = "uploadedWorldFileError";
                  saveOpenRef.current = true;
                  setSaveOpen(true);
                }
              }
            }
          };

          worldReader.readAsText(geoFile.worldFile);
        }
      }
    };

    reader.readAsDataURL(geoFile.file);
  };

  /**
   * Method to handle when the upload shp file dialog is closed.
   */
  const handleUploadFileDialogClose = (file) => {
    setOpenUploadFileDialog(false);

    if (file) {
      switch (file.type) {
        case "shp":
        case "shplayer":
          doUploadShpFile(file);
          break;

        case "geo":
        case "geoworld":
          doUploadGeoFile(file);
          break;

        default:
          break;
      }
    }
  };

  /**
   * Event to handle when the save dialog is closed.
   *
   * @param {object} event The event object
   * @param {string} reason The reason the save dialog is closing.
   * @returns
   */
  const handleSaveClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSaveOpen(false);
  };

  /**
   * Method to get the required alert text.
   *
   * @returns {string|null} the required alert text.
   */
  const getAlertText = () => {
    if (saveOpen && saveType.current) {
      switch (saveType.current) {
        case "uploadedFile":
          return `${featuresDownloaded.current} has been successfully uploaded.`;

        case "uploadedFileError":
          return `${featuresDownloaded.current} failed to be uploaded.`;

        case "uploadedRendererError":
          return `Unknown renderer in layer file: ${featuresDownloaded.current}.`;

        default:
          if (saveResult.current) return `The ${saveType.current} has been successfully saved.`;
          else if (failedValidation.current) return `Failed to validate the ${saveType.current} record.`;
          else return `Failed to save the ${saveType.current}.`;
      }
    }
  };

  /**
   * Method to apply edits to a layer.
   *
   * @param {object} editLayer The layer to apply the edits to.
   * @param {object} edits The edits to apply to the layer.
   * @param {boolean} [updateListMode=true] Whether to update the list mode of the layer.
   */
  function applyEditsToLayer(editLayer, edits, updateListMode = true) {
    editLayer
      .applyEdits(edits)
      .then((results) => {
        editLayer.queryFeatures().then((result) => {
          if (updateListMode) editLayer.listMode = result.features?.length > 0 ? "show" : "hide";
        });
      })
      .catch((error) => {
        console.error(`Error applying edits to layer ${editLayer.id}`, error);
      });
  }

  const fadeVisibilityOn = useCallback(
    (layer) => {
      let animating = true;
      let opacity = 0;
      // fade layer's opacity from 0 to
      // whichever value the user has configured
      const finalOpacity = layer.opacity;
      layer.opacity = opacity;

      if (arcgisDialogMap) {
        arcgisDialogMap.view
          .whenLayerView(layer)
          .then((layerView) => {
            function incrementOpacityByFrame() {
              if (opacity >= finalOpacity && animating) {
                animating = false;
                return;
              }

              layer.opacity = opacity;
              opacity += 0.02;

              requestAnimationFrame(incrementOpacityByFrame);
            }

            // Wait for tiles to finish loading before beginning the fade
            reactiveUtils
              .whenOnce(() => !layerView.updating)
              .then(() => {
                requestAnimationFrame(incrementOpacityByFrame);
              });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error fading layer visibility", error);
            }
          });
      }
    },
    [arcgisDialogMap]
  );

  /**
   * Method to handle when the add note button is clicked.
   */
  const handleAddNote = () => {
    selectionContext.onAddNote(true);
  };

  /**
   * Method to handle when the edit wizard button is clicked.
   *
   * @param {string} variant The variant of the wizard to open.
   */
  const handleEditWizard = (variant) => {
    selectionContext.onEditWizard(variant);
  };

  /**
   * Method to handle when the delete wizard button is clicked.
   */
  const handleDeleteWizard = () => {
    selectionContext.onDeleteWizard(true);
  };

  /**
   * Event to handle the closing of the selection box.
   */
  const handleCloseSelection = () => {
    selectionContext.onCloseSelection();
  };

  // Set the map when the component is mounted
  useEffect(() => {
    const arcgisMapElement = document.getElementById("arcgis-map-dialog");
    setArcgisDialogMap(arcgisMapElement);

    return () => {
      setArcgisDialogMap(null);
    };
  }, []);

  // Set the place on map data when the component is mounted
  useEffect(() => {
    if (placeOnMapData) {
      setPlaceStyle(placeOnMapData.placeStyle);
      setAreaStyle(placeOnMapData.areaStyle);
      setStartPoint(placeOnMapData.startPoint);
      setDirection(placeOnMapData.direction);
    }
  }, [placeOnMapData]);

  // Set point data when the data changes
  useEffect(() => {
    if (data && data.length > 0 && pointLayer.current) {
      if (
        pointData &&
        ArraysEqual(
          pointData,
          data.filter((x) => x.language === "ENG")
        )
      )
        return;

      const missingCoordinates = data.filter((x) => !x.easting && !x.northing);

      if (missingCoordinates?.length > 0) {
        const initialMidX = backgroundExtent.current
          ? (backgroundExtent.current.xmax + backgroundExtent.current.xmin) / 2
          : null;
        const initialMidY = backgroundExtent.current
          ? (backgroundExtent.current.ymax + backgroundExtent.current.ymin) / 2
          : null;

        setPointData(
          data
            .filter((x) => x.language === "ENG")
            .map((x) => {
              if (!x.easting && !x.northing) {
                return { ...x, easting: Number(initialMidX.toFixed(4)), northing: Number(initialMidY.toFixed(4)) };
              }
              return x;
            })
        );

        if (missingCoordinates.length === data.length) {
          mapContext.onWizardSetCoordinate({
            x: Number(initialMidX.toFixed(4)),
            y: Number(initialMidY.toFixed(4)),
            pointId: null,
            placeOnMapData: placeOnMapData,
          });
        } else {
          for (const updateRecord of missingCoordinates) {
            mapContext.onWizardSetCoordinate({
              x: Number(initialMidX.toFixed(4)),
              y: Number(initialMidY.toFixed(4)),
              pointId: updateRecord.PropertyId,
              placeOnMapData: placeOnMapData,
            });
          }
        }
      } else {
        setPointData(data.filter((x) => x.language === "ENG"));
      }
    } else {
      setPointData(null);
    }
  }, [data, pointData, mapContext, placeOnMapData]);

  // Load the base map layers when the map is ready
  useEffect(() => {
    if (!arcgisDialogMap) return;

    let newBaseLayer;
    const baseLayers = [];
    const referenceLayers = [];
    const galleryLayers = [];
    const baseLayerIds = [];
    const snapEsu = [];
    const snapBlpu = [];
    const snapExtent = [];
    const featureLayer = [];

    const loadBaseMapLayers = () => {
      baseMapLayers.current
        .sort((a, b) => a.layerPosition - b.layerPosition)
        .forEach((baseLayer) => {
          if (baseLayer.visible) {
            newBaseLayer = null;

            switch (baseLayer.layerType) {
              case 1: // WFS
                // Create an empty GraphicsLayer as a placeholder for when the user actually gets the features
                newBaseLayer = new GraphicsLayer({
                  id: baseLayer.layerId,
                  copyright:
                    baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                      ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                      : baseLayer.copyright,
                  title: baseLayer.title,
                  spatialReference: { wkid: spatialReferenceWkid.current },
                  listMode: baseLayer.displayInList ? "show" : "hide",
                  maxScale: baseLayer.maxScale,
                  minScale: baseLayer.minScale,
                  opacity: baseLayer.opacity,
                  visible: baseLayer.visible,
                });
                featureLayer.push({ layer: baseLayer, startIndex: 0 });
                break;

              case 2: // WMS
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    newBaseLayer = new WMSLayer({
                      url: baseLayer.url,
                      id: baseLayer.layerId,
                      customParameters: {
                        token: baseLayer.token,
                      },
                      sublayers: [
                        {
                          name: baseLayer.activeLayerId,
                          title: baseLayer.activeLayerTitle,
                        },
                      ],
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  case "viaEuropa":
                    alert("Code to handle viaEuropa WMS layers has not been written yet.");
                    break;

                  default: // OS
                    alert("Code to handle OS WMS layers has not been written yet.");
                    break;
                }
                break;

              case 3: // WMTS
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    alert("Code to handle thinkWare WMTS layers has not been written yet.");
                    break;

                  case "viaEuropa":
                    newBaseLayer = new WMTSLayer({
                      url: `${baseLayer.url}/${baseLayer.layerKey}/${baseLayer.activeLayerId}/wmts?client=arcgis`,
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  default: // OS
                    newBaseLayer = new WMTSLayer({
                      url: baseLayer.url,
                      serviceMode: baseLayer.serviceMode,
                      activeLayer: {
                        id: baseLayer.activeLayerId,
                      },
                      customParameters: {
                        key: baseLayer.layerKey,
                      },
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;
                }
                break;

              case 4: // Vector Tile
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    alert("Code to handle thinkWare Vector Tile layers has not been written yet.");
                    break;

                  case "customer":
                    alert("Code to handle customer Vector Tile layers has not been written yet.");
                    break;

                  case "Jersey":
                    newBaseLayer = new VectorTileLayer({
                      url: baseLayer.url,
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  case "viaEuropa":
                    alert("Code to handle viaEuropa Vector Tile layers has not been written yet.");
                    break;

                  default: // OS
                    alert("Code to handle OS Vector Tile layers has not been written yet.");
                    break;
                }
                break;

              default:
                break;
            }

            if (newBaseLayer) {
              baseLayerIds.push(baseLayer.layerId);
              if (baseLayer.esuSnap) snapEsu.push(baseLayer.layerId);
              if (baseLayer.blpuSnap) snapBlpu.push(baseLayer.layerId);
              if (baseLayer.extentSnap) snapExtent.push(baseLayer.layerId);
              if (baseLayer.visible) {
                if (baseLayer.esuSnap || baseLayer.blpuSnap || baseLayer.extentSnap) referenceLayers.push(newBaseLayer);
                else {
                  baseLayers.push(newBaseLayer);
                  galleryLayers.push(
                    new Basemap({
                      baseLayers: [newBaseLayer],
                      title: baseLayer.title,
                      id: baseLayer.layerId,
                      thumbnailUrl: baseLayer.thumbnailUrl,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                    })
                  );
                }
              }
            }
          }
        });

      baseMappingLayerIds.current = baseLayerIds;
      baseLayersSnapEsu.current = snapEsu;
      baseLayersSnapBlpu.current = snapBlpu;
      baseLayersSnapExtent.current = snapExtent;
      baseLayersFeature.current = featureLayer;

      const baseMap = new Basemap({
        referenceLayers: referenceLayers,
        baseLayers:
          mapContext.activeBaseMap && baseLayers.length > 0 && baseLayers.find((x) => x.id === mapContext.activeBaseMap)
            ? baseLayers.filter((x) => x.id === mapContext.activeBaseMap)
            : baseLayers.length > 0
              ? [baseLayers[0]]
              : [],
        title: "Basemaps",
        id: "dialog_basemap",
        spatialReference: { wkid: spatialReferenceWkid.current },
      });

      setBaseMapGallerySource(new LocalBasemapsSource({ basemaps: galleryLayers }));
      if (!mapContext.activeBaseMap && baseLayers.length > 0) {
        mapContext.onActiveBaseMapChange(baseLayers[0].id);
      }

      const dialogMap = new Map({
        basemap: baseMap,
      });

      arcgisDialogMap.map = dialogMap;

      backgroundStreetLayer.current = new FeatureLayer({
        id: backgroundStreetLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "USRN",
            alias: "USRN",
            type: "string",
          },
          {
            name: "EsuId",
            alias: "EsuId",
            type: "string",
          },
          {
            name: "Description",
            alias: "Description",
            type: "string",
          },
          {
            name: "State",
            alias: "State",
            type: "integer",
          },
          {
            name: "Type",
            alias: "Type",
            type: "integer",
          },
          {
            name: "StateLabel",
            alias: "StateLabel",
            type: "string",
          },
          {
            name: "TypeLabel",
            alias: "TypeLabel",
            type: "string",
          },
          {
            name: "SymbolCode",
            alias: "SymbolCode",
            type: "string",
          },
          {
            name: "Geometry",
            alias: "Geometry",
            type: "string",
          },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "polyline",
        listMode: "hide",
        popupTemplate: {
          title: "{Description}",
          lastEditInfoEnabled: false,
          content: [
            {
              type: "fields",
              fieldInfos: [
                {
                  fieldName: "EsuId",
                  label: "ESU Id",
                },
                {
                  fieldName: "USRN",
                },
                {
                  fieldName: "StateLabel",
                  label: "Status",
                },
                {
                  fieldName: "TypeLabel",
                  label: "Type",
                },
              ],
            },
          ],
        },
        renderer: streetRenderer,
        opacity: getLayerOpacity(backgroundStreetLayerTitle, true),
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: backgroundStreetLayerTitle,
        visible: getLayerVisibility(backgroundStreetLayerTitle),
      });

      let layerIndex = 0;
      arcgisDialogMap.addLayer(backgroundStreetLayer.current, layerIndex);

      backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);

      reactiveUtils.watch(
        () => backgroundStreetLayer.current.visible,
        (visible) => {
          setLayerVisibility(backgroundStreetLayerTitle, visible);
          if (visible) {
            fadeVisibilityOn(backgroundStreetLayer.current);
          }
        }
      );

      unassignedEsusLayer.current = new FeatureLayer({
        id: unassignedEsusLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "USRN",
            alias: "USRN",
            type: "string",
          },
          {
            name: "EsuId",
            alias: "EsuId",
            type: "string",
          },
          {
            name: "Description",
            alias: "Description",
            type: "string",
          },
          {
            name: "State",
            alias: "State",
            type: "integer",
          },
          {
            name: "Type",
            alias: "Type",
            type: "integer",
          },
          {
            name: "StateLabel",
            alias: "StateLabel",
            type: "string",
          },
          {
            name: "TypeLabel",
            alias: "TypeLabel",
            type: "string",
          },
          {
            name: "SymbolCode",
            alias: "SymbolCode",
            type: "string",
          },
          {
            name: "Geometry",
            alias: "Geometry",
            type: "string",
          },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "polyline",
        listMode: "hide",
        popupTemplate: {
          title: "Unassigned ESU",
          lastEditInfoEnabled: false,
          content: [
            {
              type: "fields",
              fieldInfos: [
                {
                  fieldName: "EsuId",
                  label: "ESU Id",
                },
              ],
            },
          ],
          // actions: [streetAssignAction],
          actions: [],
        },
        renderer: streetRenderer,
        opacity: getLayerOpacity(unassignedEsusLayerTitle, true),
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: unassignedEsusLayerTitle,
        visible: getLayerVisibility(unassignedEsusLayerTitle),
      });

      layerIndex++;
      arcgisDialogMap.addLayer(unassignedEsusLayer.current, layerIndex);

      unassignedEsusLayer.current.visible = getLayerVisibility(unassignedEsusLayerTitle);

      reactiveUtils.watch(
        () => unassignedEsusLayer.current.visible,
        (visible) => {
          setLayerVisibility(unassignedEsusLayerTitle, visible);
          if (visible) {
            fadeVisibilityOn(unassignedEsusLayer.current);
          }
        }
      );

      backgroundPropertyLayer.current = new FeatureLayer({
        id: backgroundPropertyLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        featureReduction: PropertyFeatureReduction(8, 15, 15, 0),
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "UPRN",
            alias: "UPRN",
            type: "string",
          },
          {
            name: "Address",
            alias: "Address",
            type: "string",
          },
          {
            name: "Postcode",
            alias: "Postcode",
            type: "string",
          },
          {
            name: "Easting",
            alias: "Easting",
            type: "double",
          },
          {
            name: "Northing",
            alias: "Northing",
            type: "double",
          },
          {
            name: "DisplayLogicalStatus",
            alias: "DisplayLogicalStatus",
            type: "integer",
          },
          {
            name: "LogicalStatus",
            alias: "LogicalStatus",
            type: "integer",
          },
          {
            name: "LogicalStatusLabel",
            alias: "LogicalStatusLabel",
            type: "string",
          },
          {
            name: "Classification",
            alias: "Classification",
            type: "string",
          },
          {
            name: "ClassificationLabel",
            alias: "ClassificationLabel",
            type: "string",
          },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "point",
        listMode: "hide",
        popupTemplate: {
          title: "{Address}",
          lastEditInfoEnabled: false,
          content: [
            {
              type: "fields",
              fieldInfos: [
                {
                  fieldName: "UPRN",
                },
                {
                  fieldName: "LogicalStatusLabel",
                  label: "Logical status",
                },
                {
                  fieldName: "ClassificationLabel",
                  label: "Classification",
                },
              ],
            },
          ],
          actions: [],
        },
        renderer: backgroundPropertyRenderer,
        opacity: getLayerOpacity(backgroundPropertyLayerTitle, true),
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: backgroundPropertyLayerTitle,
        visible: getLayerVisibility(backgroundPropertyLayerTitle),
      });

      layerIndex++;
      arcgisDialogMap.addLayer(backgroundPropertyLayer.current, layerIndex);

      backgroundPropertyLayer.current.visible = getLayerVisibility(backgroundPropertyLayerTitle);

      reactiveUtils.watch(
        () => backgroundPropertyLayer.current.visible,
        (visible) => {
          setLayerVisibility(backgroundPropertyLayerTitle, visible);
          if (visible) {
            fadeVisibilityOn(backgroundPropertyLayer.current);
          }
        }
      );

      pointLayer.current = new FeatureLayer({
        id: labelLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        labelingInfo: [
          {
            symbol: {
              type: "text",
              color: "black", //[217, 0, 182, 255],
              font: {
                family: "Noto Sans",
                size: 8,
              },
              yoffset: 8,
            },
            labelPlacement: "above-center",
            labelExpressionInfo: {
              expression: "$feature.MapLabel",
            },
            deconflictionStrategy: "none",
          },
        ],
        labelsVisible: true,
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "PropertyId",
            alias: "PropertyId",
            type: "string",
          },
          {
            name: "Easting",
            alias: "Easting",
            type: "double",
          },
          {
            name: "Northing",
            alias: "Northing",
            type: "double",
          },
          {
            name: "LogicalStatus",
            alias: "LogicalStatus",
            type: "integer",
          },
          {
            name: "Classification",
            alias: "Classification",
            type: "string",
          },
          {
            name: "MapLabel",
            alias: "Label",
            type: "string",
          },
          {
            name: "SymbolCode",
            alias: "SymbolCode",
            type: "string",
          },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "point",
        listMode: "hide",
        renderer: propertyRenderer,
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: labelLayerTitle,
      });

      layerIndex++;
      arcgisDialogMap.addLayer(pointLayer.current, layerIndex);

      if (sketchLayer.current) {
        layerIndex++;
        arcgisDialogMap.addLayer(sketchLayer.current, layerIndex);
      }

      mapContext.onAddingShpFile(mapContext.loadedShpFiles && mapContext.loadedShpFiles.length > 0);
      mapContext.onAddingGeoFile(mapContext.loadedGeoFiles && mapContext.loadedGeoFiles.length > 0);
    };

    if (!baseMapLayers.current) {
      getBaseMapLayers(userContext.current).then((result) => {
        baseMapLayers.current = result;
        if (!layersAdded.current) {
          loadBaseMapLayers();
          layersAdded.current = true;
        }
      });
    }

    return () => {};
  }, [arcgisDialogMap, fadeVisibilityOn, mapContext]);

  // Set the map extent to the start extent if it is provided
  useEffect(() => {
    if (
      startExtent &&
      arcgisDialogMap &&
      (startExtent.xmin !== backgroundExtent.current?.xmin ||
        startExtent.ymin !== backgroundExtent.current?.ymin ||
        startExtent.xmax !== backgroundExtent.current?.xmax ||
        startExtent.ymax !== backgroundExtent.current?.ymax)
    ) {
      backgroundExtent.current = startExtent;

      let newExtent = new Extent();
      newExtent.xmin = startExtent.xmin;
      newExtent.ymin = startExtent.ymin;
      newExtent.xmax = startExtent.xmax;
      newExtent.ymax = startExtent.ymax;
      newExtent.spatialReference = startExtent.spatialReference;
      arcgisDialogMap.view.extent = newExtent;
    }
  }, [startExtent, arcgisDialogMap]);

  // Background streets Layer
  useEffect(() => {
    if (!arcgisDialogMap || !backgroundStreetLayer.current) return;

    const haveStreets = mapContext.currentBackgroundData.streets && mapContext.currentBackgroundData.streets.length > 0;

    let oldAndNewSame = haveStreets
      ? ArraysEqual(
          oldBackgroundStreets.current.sort((a, b) => a.usrn - b.usrn),
          mapContext.currentBackgroundData.streets.sort((a, b) => a.usrn - b.usrn)
        )
      : false;

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomStreet === null && oldZoomBackgroundStreet.current === null) ||
        (!!mapContext.currentLayers.zoomStreet &&
          !!oldZoomBackgroundStreet.current &&
          mapContext.currentLayers.zoomStreet === oldZoomBackgroundStreet.current);
    }

    if (!haveStreets || oldAndNewSame) return;

    oldBackgroundStreets.current = mapContext.currentBackgroundData.streets;
    oldZoomBackgroundStreet.current = mapContext.currentLayers.zoomStreet;

    if (haveStreets) {
      backgroundStreetData.current = mapContext.currentBackgroundData.streets;
    } else backgroundStreetData.current = null;

    const backgroundStreetFeatures = backgroundStreetData.current
      ? backgroundStreetData.current.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: rec.usrn.toString(),
            EsuId: rec.esuId ? rec.esuId.toString() : "",
            Description: streetToTitleCase(rec.address),
            State: rec.state,
            Type: rec.type ? rec.type : 1,
            StateLabel: GetStreetStateLabel(rec.state),
            TypeLabel: GetStreetTypeLabel(rec.type ? rec.type : 1, isScottish.current),
            SymbolCode: `${rec.type ? rec.type.toString() : "1"}, ${rec.state.toString()}`,
          },
        }))
      : [];

    if (backgroundStreetLayer.current) {
      backgroundStreetLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteStreets = { deleteFeatures: result.features };
          applyEditsToLayer(backgroundStreetLayer.current, deleteStreets);
        }

        if (backgroundStreetFeatures?.length > 0) {
          const addStreets = { addFeatures: backgroundStreetFeatures };
          applyEditsToLayer(backgroundStreetLayer.current, addStreets);

          backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);
        }
      });
    }
  }, [arcgisDialogMap, mapContext, mapContext.currentBackgroundData]);

  // Unassigned ESUs layer
  useEffect(() => {
    if (!arcgisDialogMap || !unassignedEsusLayer.current) return;

    const haveEsus =
      mapContext.currentBackgroundData &&
      mapContext.currentBackgroundData.unassignedEsus &&
      mapContext.currentBackgroundData.unassignedEsus.length > 0;

    const oldAndNewSame = haveEsus
      ? ArraysEqual(oldUnassignedEsus.current, mapContext.currentBackgroundData.unassignedEsus)
      : false;

    if (!haveEsus || oldAndNewSame) return;

    oldUnassignedEsus.current = mapContext.currentBackgroundData.unassignedEsus;

    if (haveEsus) {
      unassignedEsusData.current = mapContext.currentBackgroundData.unassignedEsus;
    } else unassignedEsusData.current = null;

    const unassignedEsusFeatures = unassignedEsusData.current
      ? unassignedEsusData.current.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: "",
            EsuId: rec.esuId ? rec.esuId.toString() : "",
            Description: "",
            State: rec.state ? rec.state : 0,
            Type: 0,
            StateLabel: GetStreetStateLabel(rec.state ? rec.state : 0),
            TypeLabel: GetStreetTypeLabel(0, isScottish.current),
            SymbolCode: `0, ${rec.state ? rec.state.toString() : "0"}`,
          },
        }))
      : [];

    if (unassignedEsusLayer.current) {
      unassignedEsusLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteEsus = { deleteFeatures: result.features };
          applyEditsToLayer(unassignedEsusLayer.current, deleteEsus);
        }

        if (unassignedEsusFeatures?.length > 0) {
          const addUnassignedEsus = { addFeatures: unassignedEsusFeatures };
          applyEditsToLayer(unassignedEsusLayer.current, addUnassignedEsus);

          unassignedEsusLayer.visible = getLayerVisibility(unassignedEsusLayerTitle);
        }
      });
    }
  }, [arcgisDialogMap, mapContext, mapContext.currentBackgroundData]);

  // Background property layer
  useEffect(() => {
    if (!arcgisDialogMap || !backgroundPropertyLayer.current) return;

    const haveProperties =
      mapContext.currentBackgroundData &&
      mapContext.currentBackgroundData.properties &&
      mapContext.currentBackgroundData.properties.length > 0;

    let oldAndNewSame = haveProperties
      ? ArraysEqual(
          oldBackgroundProperties.current.sort((a, b) => a.uprn - b.uprn),
          mapContext.currentBackgroundData.properties.sort((a, b) => a.uprn - b.uprn)
        )
      : false;

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomProperty === null && oldZoomBackgroundProperty.current === null) ||
        (!!mapContext.currentLayers.zoomProperty &&
          !!oldZoomBackgroundProperty.current &&
          mapContext.currentLayers.zoomProperty === oldZoomBackgroundProperty.current);
    }

    if (!haveProperties || oldAndNewSame) return;

    oldBackgroundProperties.current = mapContext.currentBackgroundData.properties;
    oldZoomBackgroundProperty.current = mapContext.currentLayers.zoomProperty;

    if (haveProperties) {
      if (moveBlpu && pointData) {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) => pointData.findIndex((y) => Number(y.uprn) === Number(x.uprn)) === -1
        );
      } else {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties;
      }
    } else backgroundPropertyData.current = null;

    const backgroundPropertyFeatures = backgroundPropertyData.current
      ? backgroundPropertyData.current.map((rec, index) => ({
          geometry: {
            type: "point",
            x: rec.easting,
            y: rec.northing,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            UPRN: rec.uprn ? rec.uprn.toString() : "",
            Address: addressToTitleCase(rec.address, rec.postcode),
            Postcode: rec.postcode,
            Easting: rec.easting,
            Northing: rec.northing,
            DisplayLogicalStatus: backgroundPropertyData.current
              .filter((x) => Number(x.uprn) === Number(rec.uprn))
              .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus,
            LogicalStatus: rec.logicalStatus,
            LogicalStatusLabel: GetLPILogicalStatusLabel(rec.logicalStatus, isScottish.current),
            Classification: rec.blpuClass,
            ClassificationLabel: GetClassificationLabel(rec.blpuClass, isScottish.current),
          },
        }))
      : [];

    if (backgroundPropertyLayer.current) {
      backgroundPropertyLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteProperties = { deleteFeatures: result.features };
          applyEditsToLayer(backgroundPropertyLayer.current, deleteProperties);
        }

        if (backgroundPropertyFeatures?.length > 0) {
          const addProperties = { addFeatures: backgroundPropertyFeatures };
          applyEditsToLayer(backgroundPropertyLayer.current, addProperties);

          backgroundPropertyLayer.visible = getLayerVisibility(backgroundPropertyLayerTitle);
        }
      });
    }
  }, [arcgisDialogMap, mapContext, mapContext.currentBackgroundData, moveBlpu, pointData]);

  // Label and Sketch Layers
  useEffect(() => {
    if (!arcgisDialogMap || !pointLayer.current || !pointData) return;

    const getMapLabel = (rec) => {
      const duplicateCoordinates = pointData.filter((x) => x.easting === rec.easting && x.northing === rec.northing);

      if (duplicateCoordinates && duplicateCoordinates.length > 1) return `${duplicateCoordinates.length} BLPUs`;
      else return rec.addressDetails.mapLabel;
    };

    const havePoints = pointData && pointData.length > 0;

    let oldAndNewSame = havePoints
      ? ArraysEqual(
          oldPoints.current.sort((a, b) => a.uprn - b.uprn),
          pointData.sort((a, b) => a.uprn - b.uprn)
        )
      : false;

    if (!havePoints || oldAndNewSame) return;

    oldPoints.current = pointData;

    const initialMidX = backgroundExtent.current
      ? (backgroundExtent.current.xmax + backgroundExtent.current.xmin) / 2
      : null;
    const initialMidY = backgroundExtent.current
      ? (backgroundExtent.current.ymax + backgroundExtent.current.ymin) / 2
      : null;

    if (pointLayer.current) {
      const labelFeatures =
        placeStyle === "point"
          ? [
              {
                geometry: {
                  type: "point",
                  x: Number(pointData[0].easting ? pointData[0].easting : initialMidX),
                  y: Number(pointData[0].northing ? pointData[0].northing : initialMidY),
                  spatialReference: { wkid: spatialReferenceWkid.current },
                },
                attributes: {
                  ObjectID: 0,
                  PropertyId: pointData[0].id,
                  Easting: Number(pointData[0].easting ? pointData[0].easting : initialMidX),
                  Northing: Number(pointData[0].northing ? pointData[0].northing : initialMidY),
                  LogicalStatus: pointData[0].blpu.logicalStatus,
                  Classification:
                    settingsContext.isScottish && !moveBlpu
                      ? pointData[0].classification.classification
                      : pointData[0].blpu.classification,
                  MapLabel: isRange ? `${pointData.length} BLPU${pointData.length > 1 ? "s" : ""}` : "1 BLPU",
                  SymbolCode: `${pointData[0].blpu.logicalStatus}, ${
                    settingsContext.isScottish && !moveBlpu
                      ? pointData[0].classification.classification.substring(0, 1)
                      : pointData[0].blpu.classification.substring(0, 1)
                  }`,
                },
              },
            ]
          : pointData
            ? pointData.map((rec, index) => ({
                geometry: {
                  type: "point",
                  x: Number(rec.easting ? rec.easting : initialMidX),
                  y: Number(rec.northing ? rec.northing : initialMidY),
                  spatialReference: { wkid: spatialReferenceWkid.current },
                },
                attributes: {
                  ObjectID: index,
                  PropertyId: rec.id,
                  Easting: Number(rec.easting ? rec.easting : initialMidX),
                  Northing: Number(rec.northing ? rec.northing : initialMidY),
                  LogicalStatus: rec.blpu.logicalStatus,
                  Classification:
                    settingsContext.isScottish && !moveBlpu
                      ? rec.classification.classification
                      : rec.blpu.classification,
                  MapLabel: getMapLabel(rec),
                  SymbolCode: `${rec.blpu.logicalStatus}, ${
                    settingsContext.isScottish && !moveBlpu
                      ? rec.classification.classification.substring(0, 1)
                      : rec.blpu.classification.substring(0, 1)
                  }`,
                },
              }))
            : [];

      if (pointLayer.current) {
        pointLayer.current.queryFeatures().then((result) => {
          if (result.features.length > 0) {
            const deleteLabels = { deleteFeatures: result.features };
            applyEditsToLayer(pointLayer.current, deleteLabels);
          }

          if (labelFeatures?.length > 0) {
            const addLabels = { addFeatures: labelFeatures };
            applyEditsToLayer(pointLayer.current, addLabels);
          }
        });
      }

      let pointX;
      let pointY;

      if (sketchLayer.current) {
        sketchLayer.current?.graphics.removeAll();
        let newGraphics = [];

        if (placeStyle === "point") {
          pointX = data[0].easting ? data[0].easting : initialMidX;
          pointY = data[0].northing ? data[0].northing : initialMidY;

          if (pointX && pointY) {
            newGraphics.push(
              createPointGraphic(
                pointX,
                pointY,
                data[0].id,
                data[0].blpu.logicalStatus,
                settingsContext.isScottish && !moveBlpu
                  ? data[0].classification.classification.substring(0, 1)
                  : data[0].blpu.classification.substring(0, 1),
                false
              )
            );
          }
        } else if (!placeOnMapOpen.current) {
          data
            .filter((x) => x.language === "ENG")
            .sort((a, b) => Number(b.id.substring(4)) - Number(a.id.substring(4)))
            .forEach((property) => {
              pointX = property.easting ? property.easting : initialMidX;
              pointY = property.northing ? property.northing : initialMidY;

              if (pointX && pointY) {
                newGraphics.push(
                  createPointGraphic(
                    pointX,
                    pointY,
                    property.id,
                    property.blpu.logicalStatus,
                    settingsContext.isScottish && !moveBlpu
                      ? property.classification.classification.substring(0, 1)
                      : property.blpu.classification.substring(0, 1),
                    false
                  )
                );
              }
            });
        }

        if (newGraphics.length > 0) {
          sketchLayer.current.graphics.addMany(newGraphics);
        }
      }
    }
  }, [data, placeStyle, isRange, settingsContext.isScottish, moveBlpu, arcgisDialogMap, pointData]);

  // Display loaded SHP files
  useEffect(() => {
    if (!arcgisDialogMap || !mapContext.addingShpFile) return;

    mapContext.onAddingShpFile(false);

    // First unload all the shpFiles previously loaded
    if (loadedShpFileIds.current && loadedShpFileIds.current.length) {
      loadedShpFileIds.current.forEach((shpId) => {
        arcgisDialogMap.map.remove(arcgisDialogMap.map.findLayerById(shpId));
      });
    }

    if (mapContext.loadedShpFiles && mapContext.loadedShpFiles.length) {
      mapContext.loadedShpFiles.forEach((shp) => {
        const blob = new Blob([shp.geojson], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const shpLayer = new GeoJSONLayer({
          url: url,
          id: shp.id,
          copyright:
            shp.shpFile.copyright && shp.shpFile.copyright.includes("<<year>>")
              ? shp.shpFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
              : shp.shpFile.copyright,
          title: `${shp.shpFile.title}`,
          fields: shp.fields,
          objectIdField: shp.fields.find((field) => field.type === "oid")?.name,
          outFields: ["*"],
          geometryType: shp.geometryType,
          listMode: shp.shpFile.displayInList ? "show" : "hide",
          maxScale: shp.shpFile.maxScale,
          minScale: shp.shpFile.minScale,
          opacity: shp.shpFile.opacity,
          spatialReference: { wkid: spatialReferenceWkid.current },
          popupTemplate: {
            title: `{${shp.popupTitleField || shp.fields[0].name}}`,
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: shp.fields
                  .filter((f) => f.type !== "oid")
                  .map((f) => ({
                    fieldName: f.name,
                    label: f.alias,
                  })),
              },
            ],
          },
          renderer: shp.renderer,
          visible: true,
        });

        if (shp.shpFile.esuSnap) baseLayersSnapEsu.current = baseLayersSnapEsu.current.concat([shp.id]);
        if (shp.shpFile.blpuSnap) baseLayersSnapBlpu.current = baseLayersSnapBlpu.current.concat([shp.id]);
        if (shp.shpFile.extentSnap) baseLayersSnapExtent.current = baseLayersSnapExtent.current.concat([shp.id]);

        arcgisDialogMap.addLayer(shpLayer);
      });

      loadedShpFileTitles.current = mapContext.loadedShpFiles.map((x) => x.shpFile.title);
      loadedShpFileIds.current = mapContext.loadedShpFiles.map((x) => x.id);
    } else {
      loadedShpFileTitles.current = [];
      loadedShpFileIds.current = [];
    }
  }, [arcgisDialogMap, mapContext]);

  // Display loaded Geo-referenced files
  useEffect(() => {
    const getImageMetadata = (imageUrl) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = imageUrl;
      });

    const getImageCornerPoint = (a, b, c, d, e, f, x, y) => {
      const easting = a * x + b * y + c;
      const northing = d * x + e * y + f;
      return new Point({
        x: easting,
        y: northing,
        spatialReference: { wkid: spatialReferenceWkid.current },
      });
    };

    if (!arcgisDialogMap || !mapContext.addingGeoFile) return;

    mapContext.onAddingGeoFile(false);

    // First unload all the geoFiles previously loaded
    if (loadedGeoFileIds.current && loadedGeoFileIds.current.length) {
      loadedGeoFileIds.current.forEach((geoId) => {
        arcgisDialogMap.map.remove(arcgisDialogMap.map.findLayerById(geoId));
      });
    }

    if (mapContext.loadedGeoFiles && mapContext.loadedGeoFiles.length) {
      mapContext.loadedGeoFiles.forEach((geo) => {
        let geoReference = null;

        if (!geo.worldData) {
          geoReference = new ExtentAndRotationGeoreference({
            extent: new Extent({
              spatialReference: { wkid: spatialReferenceWkid.current },
              xmin: geo.geoFile.xMin,
              ymin: geo.geoFile.yMin,
              xmax: geo.geoFile.xMax,
              ymax: geo.geoFile.yMax,
            }),
          });
          const imageElement = new ImageElement({
            type: "image",
            image: geo.imageUrl,
            georeference: geoReference,
          });

          const geoLayer = new MediaLayer({
            source: imageElement,
            id: geo.id,
            copyright:
              geo.geoFile.copyright && geo.geoFile.copyright.includes("<<year>>")
                ? geo.geoFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
                : geo.geoFile.copyright,
            title: `${geo.geoFile.title}`,
            listMode: geo.geoFile.displayInList ? "show" : "hide",
            opacity: geo.geoFile.opacity,
            spatialReference: { wkid: spatialReferenceWkid.current },
            visible: true,
          });

          arcgisDialogMap.addLayer(geoLayer);
        } else {
          getImageMetadata(geo.imageUrl).then((img) => {
            geoReference = new CornersGeoreference({
              bottomRight: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                img.naturalWidth,
                img.naturalHeight
              ),
              bottomLeft: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                0,
                img.naturalHeight
              ),
              topRight: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                img.naturalWidth,
                0
              ),
              topLeft: new Point({
                x: geo.worldData[4],
                y: geo.worldData[5],
                spatialReference: { wkid: spatialReferenceWkid.current },
              }),
            });
            const imageElement = new ImageElement({
              type: "image",
              image: geo.imageUrl,
              georeference: geoReference,
            });

            const geoLayer = new MediaLayer({
              source: imageElement,
              id: geo.id,
              copyright:
                geo.geoFile.copyright && geo.geoFile.copyright.includes("<<year>>")
                  ? geo.geoFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
                  : geo.geoFile.copyright,
              title: `${geo.geoFile.title}`,
              listMode: geo.geoFile.displayInList ? "show" : "hide",
              opacity: geo.geoFile.opacity,
              spatialReference: { wkid: spatialReferenceWkid.current },
              visible: true,
            });

            arcgisDialogMap.addLayer(geoLayer);
          });
        }
      });

      loadedGeoFileTitles.current = mapContext.loadedGeoFiles.map((x) => x.geoFile.title);
      loadedGeoFileIds.current = mapContext.loadedGeoFiles.map((x) => x.id);
    } else {
      loadedGeoFileTitles.current = [];
      loadedGeoFileIds.current = [];
    }
  }, [mapContext, arcgisDialogMap]);

  // Highlight properties
  useEffect(() => {
    if (
      !arcgisDialogMap ||
      !arcgisDialogMap.layerViews ||
      arcgisDialogMap.layerViews.length === 0 ||
      !mapContext.currentHighlight ||
      !mapContext.currentHighlight.property ||
      ArraysEqual(currentHighlightedProperties.current, mapContext.currentHighlight.property)
    )
      return;

    currentHighlightedProperties.current = mapContext.currentHighlight.property;

    if (mapContext.currentHighlight.property && mapContext.currentHighlight.property.length > 0 && pointLayer.current) {
      arcgisDialogMap.view
        .whenLayerView(pointLayer.current)
        .then(function (layerView) {
          let propertyQuery = pointLayer.current.createQuery();
          propertyQuery.where = `PropertyId IN ('${mapContext.currentHighlight.property.join("', '")}')`;
          pointLayer.current.queryFeatures(propertyQuery).then(function (result) {
            if (highlightProperty.current) highlightProperty.current.remove();
            highlightProperty.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting properties: ", error);
          }
        });
    } else if (highlightProperty.current) highlightProperty.current.remove();
  }, [mapContext.currentHighlight, arcgisDialogMap]);

  useEffect(() => {
    if (initialisingMap) {
      if (mapContext.updatingData > 0 || updatingMap) {
        setMapUpdating(true);
      } else if (mapContext.updatingData === 0 && mapUpdating && !updatingMap) {
        setMapUpdating(false);
        setInitialisingMap(false);
        if (onMapUpdated) onMapUpdated();
      }
    }
  }, [mapContext.updatingData, initialisingMap, mapUpdating, updatingMap, onMapUpdated]);

  return (
    <>
      <arcgis-map
        id="arcgis-map-dialog"
        class={"arcgis-map-dialog"}
        constraints={{
          minZoom: 2,
          maxZoom: 21,
          rotationEnabled: false,
          lods: TileInfo.create({ spatialReference: { wkid: spatialReferenceWkid.current } }).lods,
        }}
      >
        <arcgis-placement position="bottom-left">
          <Box sx={{ display: "flex", gap: "0.7rem" }}>
            <Box sx={{ display: "flex", gap: "0" }}>
              <arcgis-zoom layout="horizontal"></arcgis-zoom>
              <arcgis-expand
                expandTooltip="Display basemap layer list"
                collapseIcon="chevrons-down"
                collapseTooltip="Close basemap layer list"
                group="main-toolbar"
                label="Basemap layers"
                placement="top-leading"
                closeOnEsc
              >
                <arcgis-basemap-gallery
                  id={`arcgis-basemap-gallery-dialog`}
                  class={`arcgis-basemap-gallery-dialog`}
                  // activeBasemap={mapContext.activeBaseMap}
                  icon="basemap"
                  label="Current basemap layers"
                  source={baseMapGallerySource}
                ></arcgis-basemap-gallery>
              </arcgis-expand>
              <arcgis-expand
                expandTooltip="Display layer list"
                collapseIcon="chevrons-down"
                collapseTooltip="Close layer list"
                group="main-toolbar"
                label="Layers"
                placement="top-leading"
                closeOnEsc
              >
                <arcgis-layer-list
                  id="arcgis-layer-list-dialog"
                  class={`arcgis-layer-list-dialog`}
                  label="Current layers"
                  show-heading
                  listItemCreatedFunction={defineActions}
                ></arcgis-layer-list>
              </arcgis-expand>
              <CalciteButton
                icon-start="upload"
                onClick={handleUploadFile}
                size="small"
                title="Upload file"
                kind="neutral"
                appearance="outline-fill"
              />
            </Box>
            <arcgis-scale-bar bar-style="ruler" unit="metric"></arcgis-scale-bar>
          </Box>
        </arcgis-placement>
        <arcgis-coordinate-conversion
          id="arcgis-coordinate-conversion-dialog"
          class={`arcgis-coordinate-conversion-dialog`}
          position="bottom-right"
          mode="live"
          hideSettingsButton
          label="Coordinates"
          storageType="local"
        ></arcgis-coordinate-conversion>
        <arcgis-sketch
          auto-destroy-disabled
          id="arcgis-sketch-dialog"
          class={`arcgis-sketch-dialog`}
          position="top-right"
          creation-mode="update"
          availableCreateTools={sketchCreateTools}
          defaultCreateOptions={{ hasZ: false }}
          defaultUpdateOptions={{
            tool: "reshape",
            enableRotation: false,
            enableZ: false,
            multipleSelectionEnabled: false,
            toggleToolOnClick: false,
          }}
          snappingOptions={{ enabled: true, featureSources: [] }}
          hideCreateToolsCircle
          hideCreateToolsRectangle
          hideDeleteButton
          hideDuplicateButton
          hideSnappingControls
          hideTooltipsToggle
          hideSettingsMenu
          hideSelectionCountLabel
          hideSelectionToolsLassoSelection
          hideSelectionToolsRectangleSelection
          hideUndoRedoMenu
          label="Sketch"
          scale="s"
        ></arcgis-sketch>
        <arcgis-placement position="top-left">
          <Stack direction="column" spacing={1}>
            {selectionContext.dialogSelectionCount > 0 && (
              <ADSSelectionControl
                variant={selectionContext.selectionVariant}
                selectionCount={selectionContext.dialogSelectionCount}
                onAddNote={handleAddNote}
                onEditWizard={handleEditWizard}
                onDeleteWizard={handleDeleteWizard}
                onClose={handleCloseSelection}
              />
            )}
            {informationContext.informationType && (
              <ADSInformationControl variant={informationContext.informationType} />
            )}
            {displayPlaceOnMap && (
              <Box
                id="ads-place-on-map-control"
                sx={{
                  boxShadow: `4px 4px 7px ${adsLightGreyA50}`,
                  borderRadius: "9px",
                  backgroundColor: adsWhite,
                }}
              >
                <ADSPlaceOnMapControl
                  id="place-on-map-box"
                  data={{
                    placeStyle: placeStyle,
                    areaStyle: areaStyle,
                    startPoint: startPoint,
                    direction: direction,
                  }}
                  isChild={isChild}
                  isRange={isRange}
                  onPlaceStyleChange={handlePlaceStyleChanged}
                  onAreaStyleChange={handleAreaStyleChanged}
                  onStartPointChange={handleStartPointChanged}
                  onDirectionChange={handleDirectionChanged}
                />
              </Box>
            )}
          </Stack>
        </arcgis-placement>
      </arcgis-map>
      {(mapContext.updatingData > 0 || updatingMap) && (
        <div id="progress-indicator">
          <ADSCircularProgress display={mapContext.updatingData > 0 || updatingMap} mapProgress />
        </div>
      )}
      <div>
        <Snackbar
          open={saveOpen}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleSaveClose}
        >
          <Alert
            sx={GetAlertStyle(saveResult.current)}
            icon={GetAlertIcon(saveResult.current)}
            onClose={handleSaveClose}
            severity={GetAlertSeverity(saveResult.current)}
            elevation={6}
            variant="filled"
          >
            {getAlertText()}
          </Alert>
        </Snackbar>
        <UploadShpFileDialog
          isOpen={openUploadFileDialog}
          currentIds={baseMappingLayerIds.current.concat(defaultMapLayerIds)}
          onClose={handleUploadFileDialogClose}
        />
      </div>
    </>
  );
}

export default ADSMapDialog;
